import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, CheckCircle2, AlertCircle, Phone, Mail, Users as UsersIcon, ClipboardList, ArrowRight } from 'lucide-react';
import { api, formatError, fmtDateTime } from '../api';

const TYPE_ICON = { call: Phone, email: Mail, meeting: UsersIcon, task: ClipboardList };

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function bucketFor(due) {
  const now = startOfDay(new Date());
  const day = startOfDay(due);
  const diff = Math.round((day - now) / 86400000);
  if (diff < 0) return { key: 'overdue', label: 'Overdue', color: '#f43f5e' };
  if (diff === 0) return { key: 'today', label: 'Today', color: '#4f46e5' };
  if (diff === 1) return { key: 'tomorrow', label: 'Tomorrow', color: '#0891b2' };
  if (diff <= 7) return { key: 'this_week', label: 'Later This Week', color: '#0ea5e9' };
  if (diff <= 14) return { key: 'next_week', label: 'Next Week', color: '#84cc16' };
  return { key: 'later', label: 'Later', color: '#64748b' };
}

const BUCKET_ORDER = ['overdue', 'today', 'tomorrow', 'this_week', 'next_week', 'later'];

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [deals, setDeals] = useState({});
  const [loading, setLoading] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/followups?upcoming_days=60&include_completed=${includeCompleted}`);
      setItems(data);
      const dealIds = [...new Set(data.map((f) => f.deal_id))];
      if (dealIds.length) {
        // Fetch all deals once (cheap for SMB-scale)
        const dealsRes = await api.get('/deals?limit=500');
        const map = {};
        dealsRes.data.forEach((d) => { map[d.id] = d; });
        setDeals(map);
      } else {
        setDeals({});
      }
    } catch (e) { setErr(formatError(e)); }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeCompleted]);

  const toggle = async (f) => {
    try {
      await api.put(`/followups/${f.id}`, { completed: !f.completed });
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  // Group by bucket
  const buckets = BUCKET_ORDER.reduce((acc, b) => { acc[b] = []; return acc; }, {});
  items.forEach((f) => {
    const b = bucketFor(f.due_at);
    buckets[b.key].push({ ...f, _bucket: b });
  });

  const totalOpen = items.filter((f) => !f.completed).length;
  const overdueCount = buckets.overdue.filter((f) => !f.completed).length;

  return (
    <div data-testid="crm-followups">
      <div className="crm-card" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}><CalendarClock size={16} style={{ verticalAlign: -3 }} /> Follow-ups</h3>
          <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: '4px 0 0' }}>
            <b style={{ color: 'var(--crm-ink)' }}>{totalOpen}</b> open · {overdueCount > 0 && <span style={{ color: 'var(--crm-rose)', fontWeight: 700 }}>{overdueCount} overdue</span>}
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--crm-ink)', fontWeight: 600, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} />
          Show completed
        </label>
      </div>

      {err && <div className="crm-card" style={{ color: '#9f1239', marginBottom: 12 }}>{err}</div>}

      {loading ? <div className="crm-card">Loading…</div>
        : items.length === 0 ? (
          <div className="crm-card" style={{ textAlign: 'center', padding: 40 }}>
            <CalendarClock size={32} color="#94a3b8" style={{ marginBottom: 10 }} />
            <h3 style={{ margin: 0, color: 'var(--crm-muted)' }}>No follow-ups scheduled</h3>
            <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: '6px 0 14px' }}>Open a deal to schedule your next call, email or check-in.</p>
            <Link to="/crm/pipeline" className="crm-btn">Go to Pipeline <ArrowRight size={14} /></Link>
          </div>
        ) : BUCKET_ORDER.map((bk) => {
          const list = buckets[bk];
          if (list.length === 0) return null;
          const meta = list[0]._bucket;
          return (
            <div key={bk} className="crm-card" style={{ marginBottom: 14, padding: 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                borderBottom: '1px solid var(--crm-line)',
                background: `${meta.color}10`,
              }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color }} />
                <h3 style={{ margin: 0, color: meta.color, fontSize: 14 }}>{meta.label}</h3>
                <span className="crm-pill" style={{ background: meta.color, color: '#fff', marginLeft: 6 }}>{list.length}</span>
              </div>
              <div>
                {list.map((f) => {
                  const Icon = TYPE_ICON[f.type] || CalendarClock;
                  const d = deals[f.deal_id];
                  const overdue = !f.completed && bk === 'overdue';
                  return (
                    <div key={f.id} style={{
                      display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 12,
                      padding: '12px 18px', borderBottom: '1px solid #f1f5f9', alignItems: 'center',
                      opacity: f.completed ? 0.55 : 1,
                    }} data-testid={`followup-${f.id}`}>
                      <button
                        onClick={() => toggle(f)}
                        className="crm-btn ghost sm"
                        style={{ padding: 6, borderRadius: 999, width: 32, height: 32, justifyContent: 'center' }}
                        title={f.completed ? 'Mark incomplete' : 'Mark done'}
                      >
                        <CheckCircle2 size={18} color={f.completed ? '#10b981' : '#cbd5e1'} />
                      </button>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: 'var(--crm-ink)', textDecoration: f.completed ? 'line-through' : 'none' }}>
                          <Icon size={14} color="#6366f1" />
                          <span style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(f.due_at)}</span>
                          {overdue && <AlertCircle size={13} color="#f43f5e" />}
                          <span style={{ color: 'var(--crm-muted)', fontWeight: 500 }}>· {f.type}</span>
                        </div>
                        <div style={{ fontSize: 12.5, color: 'var(--crm-ink)', marginTop: 3 }}>
                          {f.notes || <span style={{ color: 'var(--crm-muted)', fontStyle: 'italic' }}>(no notes)</span>}
                        </div>
                        {d && (
                          <Link to={`/crm/deals/${d.id}`} style={{ fontSize: 12, color: 'var(--crm-indigo)', fontWeight: 600, textDecoration: 'none' }}>
                            {d.company} — {d.project_name || 'No project'} <ArrowRight size={11} style={{ verticalAlign: -1 }} />
                          </Link>
                        )}
                      </div>
                      <div></div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
}
